import React, { useState} from 'react'
import _ from 'lodash'
import classnames from 'classnames'
import {PricingListTableHeaders} from "./PricingListTableHeaders";
import {PricingListRow} from "./PricingListRow";

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandCircleDownSharpIcon from '@mui/icons-material/ExpandCircleDownSharp';

import './PricingListPage.scss'

import {usePricingsStore} from "../../stores/pricingsStore";
import {useParams} from "react-router";
import {useUserContext} from "../../contexts/UserContext";

// Year moved out of the table into its own rail, so the table itself only
// spans Model → Actions.
const TOTAL_COLUMNS = 9
// Trim and pricing rows start one cell in: the Model column belongs to the
// model row above them.
const LEADING_BLANK_COLUMNS = 1

const TableCells = ( { times, style, className } ) => {
    return _.times(times, (n) =>
        <td
            style={style}
            key={n}
            className={className}
        />
    )
}

export const PricingListPricing = ( { pricing, trim, style_description} ) => {
    return (
        <tr className={'pricing-existing-row'}>
            <TableCells times={LEADING_BLANK_COLUMNS}/>
            <PricingListRow
                pricing={pricing}
                trim={trim}
                style_description={style_description}
            />
        </tr>
    )
}

const PricingListTrim = ( { year, trim, collapsed, style_description } ) => {
    const renderPricings = () => _.map(trim.pricings, (pricing) => {
        return (
            <PricingListPricing
                key={pricing.id}
                pricing={pricing}
                trim={trim}
                year={year}
            />
        )
    })

    if (collapsed) return null;

    return (
        <React.Fragment>
            <tr className={'pricing-trim-row'}>
                <TableCells times={LEADING_BLANK_COLUMNS}/>
                <td className={'trim-sd-name'}>
                    <div className={'trim-name'}>{trim.name}</div>
                </td>
                <PricingListRow trim={trim}/>
            </tr>
            { renderPricings() }
        </React.Fragment>
    )
}

const PricingListModel = ( { year, modelName, trims, collapsed, shouldExpand } ) => {
    const search = usePricingsStore((state) => state.search)
    const [modelCollpased, setModelCollapsed] = useState(!shouldExpand)

    const renderTrims = () => {
        return (
            _.map(trims, (trim) => {
                return (
                    <PricingListTrim
                        key={trim.id}
                        trim={trim}
                        collapsed={modelCollpased && !Boolean(search.length)}
                        year={year}
                    />
                )
            })
        )
    }
    if (collapsed) return null;

    return (
        <React.Fragment>
            <tr className={classnames('pricing-model-row', { open: !modelCollpased })}>
                    <td className={'PricingListTable-model_name'}>
                        <div className={'PricingListTable-model_name-button'} onClick={() => setModelCollapsed(!modelCollpased)}>
                            {
                                modelCollpased ?
                                    <ExpandMoreIcon
                                        className={'ExpandMoreIcon'}
                                        onClick={() => setModelCollapsed(!modelCollpased)}
                                    /> :
                                    <ExpandCircleDownSharpIcon
                                        className={'ExpandCircleUpSharpIcon'}
                                        onClick={() => setModelCollapsed(!modelCollpased)}
                                    />
                            }
                            {modelName}
                        </div>
                    </td>
                <TableCells times={TOTAL_COLUMNS - 1}/>
            </tr>
            { renderTrims() }
        </React.Fragment>
    )
}

// Models for a single year. Non-selected years render no rows but stay
// mounted, so per-model expand/collapse state survives switching years in
// the rail (same return-null-but-stay-mounted pattern the models already
// use for their trims).
const PricingListYearModels = ( { year, models, selected } ) => {
    const { profileState } = useUserContext()

    const hasPricings = (trims) => {
        if (Array.isArray(trims)) {
            return trims.some(trim => trim.pricings.length)
        }
        return false
    }

    return (
        _.map(_.omit(models, '__has_pricings'), (trims, model_name) => {
                    return <PricingListModel
                        key={model_name}
                        year={year}
                        shouldExpand={hasPricings(trims) && profileState?.is_pricing_expanded}
                        modelName={model_name}
                        trims={trims}
                        collapsed={!selected}
                    />
                }
            )
    )
}

export const PricingListTable = ( { pricingData, headerHeight } ) => {
    const params = useParams();
    const make = params.make
    const [selectedYear, setSelectedYear] = useState(null)

    // Newest year first in the rail.
    const years = _.keys(pricingData).sort((a, b) => Number(b) - Number(a))
    // A year is always selected: fall back to the newest one until the user
    // picks a year, or whenever the data no longer contains their pick
    // (brand switch, narrowed search results).
    const activeYear = years.includes(selectedYear) ? selectedYear : years[0]

    const selectYear = (year) => {
        setSelectedYear(year)
        // Deep-scrolled positions don't carry meaning across years — show the
        // newly selected year's models from the top.
        window.scrollTo({ top: 0 })
    }

    const renderYearRail = () => {
        return years.map((year) => {
            return (
                <div
                    key={year}
                    role="option"
                    aria-selected={year === activeYear}
                    className={classnames('pricing-year-rail_option', {
                        active: year === activeYear,
                        hasDeal: Boolean(pricingData[year]?.__has_pricings)
                    })}
                    onClick={() => selectYear(year)}
                >
                    {year}
                </div>
            )
        })
    }

    const renderYearModels = () => {
        return years.map((year) => {
            return (
                <PricingListYearModels
                    key={`${year}-${make}`}
                    year={year}
                    models={pricingData[year]}
                    selected={year === activeYear}
                />
            )
        })
    }

    return (
        <div className={'PricingListTable-layout'}>
            <div className={'pricing-year-rail'} role="listbox" aria-label="Year">
                <div className={'pricing-year-rail_header'}>Year</div>
                { renderYearRail() }
            </div>
            <div className={'pricing-models-panel'}>
                <table>
                    <PricingListTableHeaders headerHeight={headerHeight} />
                    <tbody>
                        {
                            renderYearModels()
                        }
                    </tbody>
                </table>
            </div>
        </div>
    )
}
